import { useAuth } from '../../hooks/useAuth';
import ChooseAthleteDialog from './ChooseAthleteDialog';
import EnterStageDialog from './EnterStageDialog';
import EntryIdentityDialog from './EntryIdentityDialog';
import type { StageEntryController } from './useStageEntry';

interface StageEntryDialogsProps {
  entry: StageEntryController;
  /** The circuit, or the competition's own name when it is nobody's stage. */
  trophyName: string;
}

/**
 * Everything that can be asked between the "enter" button and the entry, in
 * one place: the entry dialog (missing gender and date of birth, category,
 * turn), the picker for a parent who holds children's accounts — which leads
 * to the same dialog for the child picked — and the question a visitor with no
 * account gets first.
 *
 * Which one is open is the hook's decision (see useStageEntry) — three pages
 * offer the same button, and none of them should have to know that a parent
 * gets a different screen from a climber.
 */
export default function StageEntryDialogs({ entry, trophyName }: StageEntryDialogsProps) {
  const { user } = useAuth();
  const email = user?.email ?? '';

  return (
    <>
      <EnterStageDialog
        stage={entry.mode === 'confirm' ? entry.pending : null}
        trophyName={trophyName}
        athleteName={entry.forAthlete?.full_name}
        forUserId={entry.forAthlete?.user_id ?? null}
        initialRoundId={entry.initialRoundId}
        email={email}
        entering={entry.entering}
        refusal={entry.refusal}
        onEdited={entry.clearRefusal}
        onCancel={entry.cancel}
        onConfirm={entry.confirm}
      />

      <ChooseAthleteDialog
        stage={entry.mode === 'pick' ? entry.pending : null}
        trophyName={trophyName}
        athletes={entry.athletes}
        email={email}
        entering={entry.entering}
        onCancel={entry.cancel}
        onConfirm={entry.confirmAthlete}
        onWardCreated={entry.onWardCreated}
        onWardUpdated={entry.onWardUpdated}
      />

      <EntryIdentityDialog
        stage={entry.mode === 'identity' ? entry.pending : null}
        onCancel={entry.cancel}
        onChoose={entry.chooseIdentity}
      />
    </>
  );
}
